import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Loader2, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  login,
  register as registerApi,
  useAuthStore,
  isApiError,
  type ApiError,
} from '@fog-chess/core';
import { connectGameSocket } from '@/lib/socket';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

/**
 * AuthScreen - dual-tab Login / Register.
 *
 * - Zod validation mirrors the backend: username 3-32 chars
 *   `^[A-Za-z0-9_]+$`; password >= 8.
 * - Submissions hit apiClient /auth/login + /auth/register.
 * - On success: populate useAuthStore (in-memory token + persisted user),
 *   bootstrap the singleton game socket (so the GlobalMatchmakingListener
 *   starts receiving pushes immediately), then navigate to the originally
 *   requested page (state.from) or "/".
 * - The auth store never touches server-state; the score needed for gate
 *   locking is fetched separately by screens via TanStack Query.
 */

const USERNAME_RE = /^[A-Za-z0-9_]+$/;

const loginSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(32, 'Username must be at most 32 characters')
    .regex(
      USERNAME_RE,
      'Username may only contain letters, numbers and underscores',
    ),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});
type LoginValues = z.infer<typeof loginSchema>;

// Register form adds an explicit confirm-password field. We do NOT send it
// to the backend; it gates client-side parity only.
const registerSchema = loginSchema
  .extend({
    confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type RegisterValues = z.infer<typeof registerSchema>;

const WS_URL =
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: Record<string, string> }).env
      ?.VITE_WS_URL) ||
  'http://localhost:8000';

/**
 * Translate ApiError into a friendly toast message keyed by HTTP status /
 * error_code. Falls back to the raw detail string.
 */
function describeAuthError(error: unknown): string {
  if (isApiError(error)) {
    const apiErr = error as ApiError;
    if (apiErr.status === 401) return 'Invalid username or password.';
    if (apiErr.status === 409) return 'That username is already taken.';
    if (apiErr.status === 422) return 'Please correct the highlighted fields.';
    if (apiErr.isNetworkError) return 'Network error — check your connection.';
    return apiErr.message || 'Authentication failed.';
  }
  if (error instanceof Error) return error.message;
  return 'Authentication failed.';
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12">
      {/* Ambient fog backdrop */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1 }}
        className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-fog/20 blur-3xl"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.2, delay: 0.1 }}
        className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg">
            <Swords className="h-6 w-6" />
          </span>
          <h1 className="text-3xl font-bold tracking-tight">Fog Chess</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Outwit the fog. Climb the FTPES ladder.
          </p>
        </div>
        <Card className="border-border/60 bg-card/80 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-card/60">
          {children}
        </Card>
      </motion.div>
    </div>
  );
}

export function AuthScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Read the post-redirect target the guard stashed in location.state.
  const from =
    (location.state as { from?: string } | null)?.from ?? '/';

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const registerForm = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { username: '', password: '', confirmPassword: '' },
  });

  const finishAuth = async () => {
    // Bootstrap the singleton socket so the GlobalMatchmakingListener can
    // receive match:found for the lifetime of the session. A failed socket
    // bootstrap is non-fatal for auth itself (the user can still browse);
    // we surface it as a warning so they know matchmaking will retry.
    const token = useAuthStore.getState().accessToken;
    if (!token) {
      navigate(from, { replace: true });
      return;
    }
    try {
      await connectGameSocket(token, WS_URL);
    } catch (socketError) {
      // eslint-disable-next-line no-console
      console.warn('[auth] socket bootstrap deferred:', socketError);
      toast.warning(
        'Signed in, but the live connection is still warming up. Matchmaking will reconnect automatically.',
      );
    }
    navigate(from, { replace: true });
  };

  const onLogin = async (values: LoginValues) => {
    setIsSubmitting(true);
    try {
      const envelope = await login(values);
      setAuth(envelope);
      toast.success(`Welcome back, ${envelope.user.username}.`);
      await finishAuth();
    } catch (error) {
      toast.error(describeAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRegister = async (values: RegisterValues) => {
    setIsSubmitting(true);
    try {
      const envelope = await registerApi({
        username: values.username,
        password: values.password,
      });
      setAuth(envelope);
      toast.success(`Account created. Welcome, ${envelope.user.username}.`);
      await finishAuth();
    } catch (error) {
      toast.error(describeAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <CardHeader className="space-y-1">
        <CardTitle className="text-center text-2xl">Sign in to play</CardTitle>
        <CardDescription className="text-center">
          Choose how you enter the fog.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={tab}
          onValueChange={(v) =>
            setTab(v === 'register' ? 'register' : 'login')
          }
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" disabled={isSubmitting}>
              Login
            </TabsTrigger>
            <TabsTrigger value="register" disabled={isSubmitting}>
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-4">
            <Form {...loginForm}>
              <form
                onSubmit={loginForm.handleSubmit(onLogin)}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={loginForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="username"
                          placeholder="fog_master"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          placeholder="••••••••"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isSubmitting ? (
                      <motion.span
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Signing in…
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <LogIn className="h-4 w-4" />
                        Sign in
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="register" className="mt-4">
            <Form {...registerForm}>
              <form
                onSubmit={registerForm.handleSubmit(onRegister)}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={registerForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="username"
                          placeholder="fog_master"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        3–32 characters: letters, numbers, underscores.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={registerForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder="••••••••"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>At least 8 characters.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={registerForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder="••••••••"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isSubmitting ? (
                      <motion.span
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating account…
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <UserPlus className="h-4 w-4" />
                        Create account
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </form>
            </Form>
          </TabsContent>
        </Tabs>

        <AnimatePresence>
          {tab === 'login' ? (
            <motion.p
              key="toRegister"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-center text-sm text-muted-foreground"
            >
              New to the fog?{' '}
              <button
                type="button"
                onClick={() => setTab('register')}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Create an account
              </button>
            </motion.p>
          ) : (
            <motion.p
              key="toLogin"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-center text-sm text-muted-foreground"
            >
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setTab('login')}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Sign in
              </button>
            </motion.p>
          )}
        </AnimatePresence>
      </CardContent>
    </AuthShell>
  );
}

